import {createContext, ReactNode, useContext, useMemo} from "react";
import {CheckoutApi} from "../api/CheckoutApi";
import {CheckoutConfig} from "../api/models";
import {useAsync} from "./useAsync";

interface ShopState {
    // Whether the shop is open: the master switch (Administration > Shop) is on. False until the API says so, and when
    // it can't be asked, so the site never offers to sell by mistake.
    open: boolean;
    // The API's answer: the switch, and which payment providers are set up. Undefined until it's loaded.
    config: CheckoutConfig | undefined;
    // After the switch is flipped, so every page follows.
    setConfig(config: CheckoutConfig): void;
}

const ShopContext = createContext<ShopState | null>(null);

// Asks the API once per page load whether the shop is open; the home page, the menus and the product pages follow it.
export function ShopProvider({children}: { children: ReactNode }) {
    const {data: config, setData} = useAsync(() => CheckoutApi.config(), []);
    const state = useMemo<ShopState>(() => ({ open: config?.enabled === true, config, setConfig: setData }), [config, setData]);
    return <ShopContext.Provider value={state}>{children}</ShopContext.Provider>;
}

export function useShop(): ShopState {
    const state = useContext(ShopContext);
    if (!state) {
        throw new Error('useShop needs a ShopProvider');
    }
    return state;
}
